import { lentComponent, useContractMutation, useContractQuery, useInvalidateContract } from "@amtana/browser";
import type { LentToken, MutationResult, QueryResult } from "@amtana/browser";
import { projectTrpcContract } from "@amtana/project-contract";
import type {
  ArchiveProjectsInput,
  ArchiveProjectsOutput,
  CreateProjectInput,
  ListProjectsInput,
  Project,
  ProjectList,
  RenameProjectInput,
  SelectProjectInput,
  UnarchiveProjectInput,
} from "@amtana/project-contract";

// Projects for every browser module: the list, the current one, and the writes.
// A write refreshes every read, since switching project rescopes most screens.

export function useProjects(input: ListProjectsInput = {}): QueryResult<ProjectList> {
  return useContractQuery(projectTrpcContract, "list", { input });
}

function useWrite<TName extends "create" | "rename" | "archive" | "unarchive" | "select", TInput, TOutput>(
  name: TName,
): MutationResult<TOutput, TInput> {
  const mutation = useContractMutation(projectTrpcContract, name);
  const invalidate = useInvalidateContract();
  // The contract types `name`'s input and output; the hook restates them for consumers.
  const typed = mutation as unknown as MutationResult<TOutput, TInput>;
  return { ...typed, run: (input: TInput) => typed.mutateAsync(input).finally(() => void invalidate()) };
}

export const useCreateProject = (): MutationResult<Project, CreateProjectInput> => useWrite("create");
export const useRenameProject = (): MutationResult<Project, RenameProjectInput> => useWrite("rename");
export const useArchiveProjects = (): MutationResult<ArchiveProjectsOutput, ArchiveProjectsInput> =>
  useWrite("archive");
export const useUnarchiveProject = (): MutationResult<Project, UnarchiveProjectInput> =>
  useWrite("unarchive");
export const useSelectProject = (): MutationResult<Project, SelectProjectInput> => useWrite("select");

/** The top bar's project switcher: pick the current project, or start a new one. */
export const ProjectSwitcher: LentToken<Record<string, never>> = lentComponent<Record<string, never>>(
  "project",
  "ProjectSwitcher",
);
